// Lightweight GA4 event helper for the marketing site.
//
// The GA gtag snippet lives in layouts/marketing.html.erb (id G-2C66GR1H2R).
// This wrapper is safe to call during SSR (no `window`) and before gtag has
// finished loading — in that case the event is queued on `dataLayer` and picked
// up once GA initialises. Analytics must never break a user interaction, so
// everything here is defensive and swallows its own errors.

export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    } else {
      window.dataLayer.push({ event: name, ...params });
    }
  } catch (e) {
    /* no-op: analytics must never throw into a click handler */
  }
}

// A CTA counts as a trial/sign-up start if it points at the sign-up flow.
const SIGNUP_HINTS = ['/users/sign_up', 'sign_up', 'apply-to-sell'];

export function isTrialCta(href) {
  return typeof href === 'string' && SIGNUP_HINTS.some((hint) => href.includes(hint));
}

// Shared event params. Labels are only kept when they're plain strings (a
// React node would stringify to "[object Object]"), capped at GA's 100 chars.
const ctaLabel = (label) => (typeof label === 'string' ? label.slice(0, 100) : '');
const currentPath = () => (typeof window !== 'undefined' ? window.location.pathname : undefined);

// GA4 conversion event fired when a visitor clicks through to start a trial.
// Mark `begin_trial` as a Key Event (conversion) in GA4 to attribute organic
// traffic and landing pages to trial sign-ups.
export function trackTrialCta(href, label) {
  trackEvent('begin_trial', {
    method: 'marketing_cta',
    cta_label: ctaLabel(label),
    destination: href,
    page_path: currentPath(),
  });
}

// Shopstar Go (www.shopstar.io) is a separate platform; any link to it counts
// as a Go click-through.
export function isShopstarGoLink(href) {
  return typeof href === 'string' && href.includes('shopstar.io');
}

// Pull `utm_medium` (the placement, e.g. 'pricing_card') off a Go link. Regex
// rather than URL/window so it also works during SSR and on malformed hrefs.
function utmMedium(href) {
  try {
    const match = /[?&]utm_medium=([^&#]*)/.exec(href);
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch (e) {
    return undefined;
  }
}

// GA4 event fired when a visitor clicks through to Shopstar Go, so we can see
// which marketing surfaces (by utm_medium) send traffic to the new platform.
export function trackGoClick(href, label) {
  trackEvent('shopstar_go_click', {
    cta_label: ctaLabel(label),
    destination: href,
    utm_medium: utmMedium(href),
    page_path: currentPath(),
  });
}
